import { useState } from 'react';

import { bootstrapMediaDevices } from 'features/media-devices/services';
import { useAppDispatch } from 'features/app/hooks';
import { useLocalStorage } from 'features/media-devices/hooks';
import type {
  MediaDevice,
  MediaDeviceGroup,
} from 'features/media-devices/interfaces';
import {
  cameraSelected,
  microphoneSelected,
  speakerSelected,
} from 'features/media-devices/store';
import { handleError } from 'features/notification/store';
import type { SelectItem } from 'features/ui/elements';

export const useMediaDevices = () => {
  const dispatch = useAppDispatch();
  const [storageDevices, setStorageDevices] = useLocalStorage<MediaDeviceGroup>(
    'userDevices',
    {} as MediaDeviceGroup
  );
  const [cameraList, setCameraList] = useState<SelectItem[]>([]);
  const [microphoneList, setMicrophoneList] = useState<SelectItem[]>([]);
  const [speakerList, setSpeakerList] = useState<SelectItem[]>([]);

  const handleCameraChange = (camera: MediaDevice) => {
    dispatch(cameraSelected(camera));
    setStorageDevices((devices) => ({ ...devices, camera }));
  };
  const handleMicrophoneChange = (microphone: MediaDevice) => {
    dispatch(microphoneSelected(microphone));
    setStorageDevices((devices) => ({ ...devices, microphone }));
  };
  const handleSpeakerChange = (speaker: MediaDevice) => {
    dispatch(speakerSelected(speaker));
    setStorageDevices((devices) => ({ ...devices, speaker }));
  };

  const getDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const storedDevices = getLatestStoredDevices();
      const bootstrap = bootstrapMediaDevices({
        devices,
        storedDevices,
      });

      setMicrophoneList(bootstrap.microphoneList);
      setCameraList(bootstrap.cameraList);
      setSpeakerList(bootstrap.speakerList);
      dispatch(cameraSelected(bootstrap.selected.camera));
      dispatch(microphoneSelected(bootstrap.selected.microphone));
      dispatch(speakerSelected(bootstrap.selected.speaker));
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'getDevices',
        })
      );
    }
  };

  const getLatestStoredDevices = (): MediaDeviceGroup => {
    try {
      const currentStoredDevices = localStorage.getItem('userDevices');

      return currentStoredDevices
        ? (JSON.parse(currentStoredDevices) as MediaDeviceGroup)
        : storageDevices;
    } catch {
      return storageDevices;
    }
  };

  return {
    getDevices,
    cameraList,
    speakerList,
    microphoneList,
    handleCameraChange,
    handleSpeakerChange,
    handleMicrophoneChange,
  };
};
